import { useCallback, useEffect, useState } from "react";
import { useManagerScope } from "@/components/ManagerLayout";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check, X, Download, CalendarDays, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { Pagination, usePagination } from "@/components/Pagination";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

/** Pill colour for a leave-request status. */
function leaveStatusBadgeClass(status) {
  if (status === "PENDING") return "bg-amber-50 text-amber-800 border-amber-200";
  if (status === "APPROVED") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  return "bg-red-50 text-red-800 border-red-200";
}

/** Pill colour for the secondary Xero-sync status under an APPROVED row. */
function xeroPushBadgeClass(status) {
  const base = "text-[10px]";
  if (status === "SYNCED") return `bg-emerald-50 text-emerald-700 border-emerald-200 ${base}`;
  if (status === "FAILED") return `bg-red-50 text-red-700 border-red-200 ${base}`;
  return `bg-amber-50 text-amber-700 border-amber-200 ${base}`;
}

/** Friendly text for the secondary Xero-sync pill. */
function xeroPushBadgeLabel(status) {
  const map = {
    SYNCED: "Synced · Xero",
    FAILED: "Xero: failed",
    SKIPPED_NO_CALENDAR: "Xero: no calendar",
    SKIPPED_NO_XERO_EMPLOYEE: "Xero: not linked",
    SKIPPED_NO_CONNECTION: "Xero: offline",
  };
  return map[status] || `Xero: ${(status || "").toLowerCase().replace(/_/g, " ")}`;
}

// Friendly summaries of the Xero short-circuit codes so the manager
// understands *why* the push was/wasn't completed.
const XERO_PUSH_TOAST = {
  SYNCED: { kind: "success", text: "Approved · synced to Xero" },
  DRY_RUN: { kind: "message", text: "Approved (dry-run only)" },
  AUTO_SYNC_DISABLED: { kind: "message", text: "Approved · auto-sync is OFF" },
  SKIPPED_NO_CONNECTION: { kind: "warning", text: "Approved · Xero is not connected. Ask an admin to connect Xero." },
  SKIPPED_NO_XERO_EMPLOYEE: { kind: "warning", text: "Approved · this employee isn't linked to a Xero record yet" },
  SKIPPED_NO_CALENDAR: { kind: "warning", text: "Approved · this employee has no Xero payroll calendar" },
  SKIPPED_NO_TYPE_MATCH: { kind: "warning", text: "Approved · couldn't match this leave type in Xero" },
  FAILED: { kind: "error", text: "Approved, but Xero push FAILED. Retry from Admin · Leave." },
};

function ApproveDialog({ leave, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null); // null | "approve" | "approve_xero"

  const submit = async (pushXero) => {
    setBusy(pushXero ? "approve_xero" : "approve");
    try {
      const url = `/manager/leave/${leave.id}/approve${pushXero ? "?push_xero=true" : ""}`;
      const { data } = await api.post(url, { note });
      const xeroStatus = data?.xero?.status;
      const meta = XERO_PUSH_TOAST[xeroStatus];
      if (pushXero && xeroStatus === "SYNCED") {
        toast.success(meta.text);
      } else if (pushXero && meta) {
        if (meta.kind === "warning") toast.warning(meta.text, { description: data?.xero?.error });
        else if (meta.kind === "error") toast.error(meta.text, { description: data?.xero?.error });
        else toast.message(meta.text);
      } else {
        toast.success("Leave approved");
      }
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${leave.id}`}>
          <Check className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Approve {leave.employee_name}'s leave</DialogTitle></DialogHeader>
        <div className="text-sm text-gray-600">{leave.leave_type} · {fmtDate(leave.start_date)} → {fmtDate(leave.end_date)} · {Number(leave.total_hours ?? leave.working_days ?? leave.days ?? 0).toFixed(1)} hrs</div>
        <div className="space-y-2">
          <Label>Note (optional)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid={`approve-note-${leave.id}`} />
        </div>
        <p className="text-[11px] text-gray-500 leading-relaxed">
          <span className="font-semibold text-[#1A2744]">Approve & sync to Xero</span> closes the payroll loop in one click —
          works only if this employee is linked to a Xero record with a payroll calendar.
          Otherwise use plain <span className="font-semibold">Approve</span>; an admin can push later from Admin · Xero.
        </p>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy !== null}>Cancel</Button>
          <Button
            variant="outline"
            onClick={() => submit(false)}
            disabled={busy !== null}
            data-testid={`approve-only-${leave.id}`}
          >
            {busy === "approve" ? "Approving…" : "Approve only"}
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={busy !== null}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid={`approve-and-sync-${leave.id}`}
          >
            <Send className="h-3.5 w-3.5 mr-1" />
            {busy === "approve_xero" ? "Approving & syncing…" : "Approve & sync to Xero"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ leave, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/manager/leave/${leave.id}/decline`, { reason });
      toast.success("Leave declined");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`decline-${leave.id}`}>
          <X className="h-3.5 w-3.5 mr-1" /> Decline
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Decline {leave.employee_name}'s leave</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Reason <span className="text-red-500">*</span></Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Required — the employee will see this." data-testid={`decline-reason-${leave.id}`} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 text-white hover:bg-red-700">{busy ? "Declining…" : "Decline"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ManagerLeave() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [view, setView] = useState("PENDING");
  const { scopeToken } = useManagerScope();

  const load = useCallback(async () => {
    const url = view === "PENDING" ? "/manager/leave/pending" : "/manager/leave";
    const { data } = await api.get(url, view === "PENDING" ? {} : { params: {} });
    setItems(data.leave);
    setSelected(new Set());
  }, [view]);

  useEffect(() => { load(); }, [load, scopeToken]);

  const pg = usePagination(items, 10);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const bulkApprove = async () => {
    if (!selected.size) return;
    try {
      const { data } = await api.post("/manager/leave/bulk-approve", { leave_ids: Array.from(selected) });
      toast.success(`${data.approved.length} approved${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="mgr-leave-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">Leave</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] mt-1">Your team's time off</h1>
          <p className="text-sm text-gray-500 mt-1">Approve or decline requests. Your own leave routes to admin.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/manager/leave/heatmap">
            <Button variant="outline" size="sm" data-testid="link-heatmap"><CalendarDays className="h-4 w-4 mr-1" /> Year heatmap</Button>
          </Link>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/manager/leave/export.csv`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="export-leave"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
          </a>
        </div>
      </header>

      <div className="flex gap-2 border-b border-gray-200">
        {["PENDING", "ALL"].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-3 py-2 text-sm font-medium ${view === v ? "text-[#1A2744] border-b-2 border-[#C9A84C]" : "text-gray-500"}`}
            data-testid={`tab-${v.toLowerCase()}`}
          >
            {v === "PENDING" ? "Pending" : "All history"}
          </button>
        ))}
      </div>

      {view === "PENDING" && selected.size > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/30">
          <span className="text-sm text-[#1A2744]">{selected.size} selected</span>
          <Button size="sm" onClick={bulkApprove} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="bulk-approve">
            <Check className="h-4 w-4 mr-1" /> Approve selected
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {view === "PENDING" && <TableHead className="w-8"></TableHead>}
                <TableHead>Employee</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Conflicts</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-8">
                  {view === "PENDING" ? "No pending leave requests. Your team is all set." : "No leave requests."}
                </TableCell></TableRow>
              )}
              {pg.pageItems.map((l) => (
                <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                  {view === "PENDING" && (
                    <TableCell>
                      <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`select-${l.id}`} />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="font-medium text-[#1A2744]">{l.employee_name}</div>
                    <div className="text-[11px] text-gray-500">{l.employee_code}</div>
                  </TableCell>
                  <TableCell className="capitalize text-sm">{(l.leave_type || "").toLowerCase().replace("_", " ")}</TableCell>
                  <TableCell className="text-sm">
                    <div>{fmtDate(l.start_date)}</div>
                    <div className="text-[11px] text-gray-500">→ {fmtDate(l.end_date)}</div>
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {l.total_hours != null
                      ? <>{Number(l.total_hours).toFixed(1)}<span className="text-[10px] text-gray-400 ml-1">hrs</span></>
                      : (l.working_days || l.days || "—")}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {l.current_balance !== undefined ? (
                      <span className={l.current_balance < (l.total_hours ?? l.working_days ?? 0) ? "text-red-600 font-semibold" : ""}>
                        {Number(l.current_balance).toFixed(1)}<span className="text-[10px] text-gray-400 ml-1">hrs</span>
                      </span>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    {(l.conflicts || []).length > 0 ? (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                        {l.conflicts.length} overlap
                      </Badge>
                    ) : <span className="text-xs text-gray-400">none</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant="outline" className={leaveStatusBadgeClass(l.status)}>{l.status}</Badge>
                      {l.status === "APPROVED" && l.xero_sync_status && l.xero_sync_status !== "NONE" && (
                        <Badge
                          variant="outline"
                          title={l.xero_last_error || ""}
                          className={xeroPushBadgeClass(l.xero_sync_status)}
                          data-testid={`mgr-xero-status-${l.id}`}
                        >
                          {xeroPushBadgeLabel(l.xero_sync_status)}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {l.status === "PENDING" && (
                      <div className="flex gap-1 flex-wrap">
                        <ApproveDialog leave={l} onDone={load} />
                        <DeclineDialog leave={l} onDone={load} />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="mgr-leave" />
    </div>
  );
}
